import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Loader2, Search, TrendingUp, TrendingDown, DollarSign,
  ArrowUpRight, ArrowDownRight, Download, Plus, Receipt, Wallet, FileText, ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { exportToCSV } from "@/lib/export-utils";
import AddInvoiceDialog from "@/components/dialogs/AddInvoiceDialog";
import AddExpenseDialog from "@/components/dialogs/AddExpenseDialog";

const PIE_COLORS = ["hsl(142 71% 45%)", "hsl(48 96% 53%)", "hsl(0 84% 60%)"];
const months = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];

const txTypeLabels: Record<string, string> = {
  invoice_payment: "دفع فاتورة",
  salary: "راتب",
  expense: "مصروف",
  refund: "استرداد",
  subscription: "اشتراك",
};

const salaryTypeLabels: Record<string, string> = {
  monthly: "شهري",
  per_session: "بالحصة",
  percentage: "نسبة",
};

const invoiceStatusLabels: Record<string, string> = {
  paid: "مدفوع",
  pending: "معلق",
  overdue: "متأخر",
  review: "مراجعة",
};

const FinancialCenter = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const nurseryId = profile?.nursery_id;

  // --- Queries ---
  const { data: invoices, isLoading: loadingInv, refetch: refetchInv } = useQuery({
    queryKey: ["fc-invoices", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("invoices")
        .select("*, children(full_name)")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false })
        .limit(500);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: expenses, isLoading: loadingExp } = useQuery({
    queryKey: ["fc-expenses", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("expenses")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .order("expense_date", { ascending: false })
        .limit(500);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: payroll, isLoading: loadingPay } = useQuery({
    queryKey: ["fc-payroll", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("payroll_records")
        .select("*, staff(full_name, role, salary_type)")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false })
        .limit(500);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: transactions, isLoading: loadingTx } = useQuery({
    queryKey: ["fc-transactions", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("financial_transactions")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .order("transaction_date", { ascending: false })
        .limit(500);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: children } = useQuery({
    queryKey: ["fc-children", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("children")
        .select("id, full_name, monthly_fee, enrollment_date")
        .eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: feeTemplates } = useQuery({
    queryKey: ["fc-fee-templates", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("fee_templates")
        .select("*")
        .eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const isLoading = loadingInv || loadingExp || loadingPay || loadingTx;

  // --- Summary stats ---
  const stats = useMemo(() => {
    const totalRevenue = (invoices || []).filter(i => i.status === "paid").reduce((s, i) => s + Number(i.amount), 0);
    const totalExpenses = (expenses || []).reduce((s, e) => s + Number(e.amount), 0);
    const totalPayroll = (payroll || []).filter(p => p.status === "paid").reduce((s, p) => s + Number(p.net_salary || 0), 0);
    const totalOut = totalExpenses + totalPayroll;
    const paidInvoices = totalRevenue;
    const overdueInvoices = (invoices || []).filter(i => i.status === "overdue").reduce((s, i) => s + Number(i.amount), 0);
    return { totalRevenue, totalOut, net: totalRevenue - totalOut, paidInvoices, overdueInvoices };
  }, [invoices, expenses, payroll]);

  // --- Monthly chart data ---
  const monthlyData = useMemo(() => {
    if (!transactions) return [];
    const map: Record<string, { month: string; income: number; expense: number }> = {};
    transactions.forEach(t => {
      const d = new Date(t.transaction_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = `${months[d.getMonth()]} ${d.getFullYear()}`;
      if (!map[key]) map[key] = { month: label, income: 0, expense: 0 };
      if (t.direction === "in") map[key].income += Number(t.amount);
      else map[key].expense += Number(t.amount);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).slice(-6).map(([, v]) => v);
  }, [transactions]);

  // --- Invoice status pie ---
  const invoicePie = useMemo(() => {
    if (!invoices) return [];
    const paid = invoices.filter(i => i.status === "paid").length;
    const pending = invoices.filter(i => i.status === "pending").length;
    const overdue = invoices.filter(i => i.status === "overdue").length;
    return [
      { name: "مدفوع", value: paid },
      { name: "معلق", value: pending },
      { name: "متأخر", value: overdue },
    ].filter(d => d.value > 0);
  }, [invoices]);

  // --- Transaction filters ---
  const [txSearch, setTxSearch] = useState("");
  const [txType, setTxType] = useState("all");
  const [txDir, setTxDir] = useState("all");

  const filteredTx = useMemo(() => {
    if (!transactions) return [];
    return transactions.filter(t => {
      if (txSearch && !(t.description || "").includes(txSearch) && !(t.staff_name || "").includes(txSearch)) return false;
      if (txType !== "all" && t.transaction_type !== txType) return false;
      if (txDir !== "all" && t.direction !== txDir) return false;
      return true;
    });
  }, [transactions, txSearch, txType, txDir]);

  const handleExportTx = () => {
    if (!filteredTx.length) return;
    exportToCSV(filteredTx.map(t => ({
      "النوع": txTypeLabels[t.transaction_type] || t.transaction_type,
      "الاتجاه": t.direction === "in" ? "وارد" : "صادر",
      "المبلغ": t.amount,
      "الوصف": t.description || "",
      "التاريخ": t.transaction_date,
    })), "financial-ledger");
  };

  const summaryCards = [
    { label: "الإيرادات", value: stats.totalRevenue, icon: ArrowUpRight, color: "text-green-600" },
    { label: "المصروفات", value: stats.totalOut, icon: ArrowDownRight, color: "text-destructive" },
    { label: "صافي الربح", value: stats.net, icon: stats.net >= 0 ? TrendingUp : TrendingDown, color: stats.net >= 0 ? "text-primary" : "text-destructive" },
    { label: "المدفوع", value: stats.paidInvoices, icon: DollarSign, color: "text-green-600" },
    { label: "المتأخر", value: stats.overdueInvoices, icon: DollarSign, color: "text-destructive" },
  ];

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">المركز المالي</h1>
        <p className="text-muted-foreground text-sm mt-1">نظرة شاملة على الوضع المالي للحضانة</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {summaryCards.map(c => (
          <Card key={c.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <c.icon className={`w-5 h-5 ${c.color}`} />
              </div>
              <div className="min-w-0">
                <div className={`text-lg font-bold truncate ${c.color}`}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `${c.value.toLocaleString()} ج.م`}
                </div>
                <div className="text-xs text-muted-foreground">{c.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <AddInvoiceDialog
          children={children || []}
          feeTemplates={feeTemplates || []}
          onSuccess={() => refetchInv()}
        />
        <AddExpenseDialog onSuccess={() => {}} />
        <Button variant="outline" onClick={() => navigate("/dashboard/salary-report")}>
          <Wallet className="w-4 h-4 ml-2" />
          صرف راتب
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="overview">نظرة عامة</TabsTrigger>
          <TabsTrigger value="invoices">الفواتير</TabsTrigger>
          <TabsTrigger value="payroll">الرواتب</TabsTrigger>
          <TabsTrigger value="transactions">السجل المالي</TabsTrigger>
        </TabsList>

        {/* ===== OVERVIEW ===== */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            {monthlyData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-lg">الإيرادات الشهرية</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(val: number) => `${val.toLocaleString()} ج.م`} />
                        <Legend />
                        <Bar dataKey="income" name="إيرادات" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
                        <Bar dataKey="expense" name="مصروفات" fill="hsl(var(--destructive))" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            {invoicePie.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-lg">توزيع الفواتير</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={invoicePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                          {invoicePie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
            {monthlyData.length === 0 && invoicePie.length === 0 && !isLoading && (
              <div className="col-span-2 text-center py-12 text-muted-foreground">لا توجد بيانات كافية لعرض الرسوم البيانية</div>
            )}
          </div>
        </TabsContent>

        {/* ===== INVOICES ===== */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">أحدث الفواتير</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/billing")}>
                عرض الكل <ArrowRight className="w-4 h-4 mr-1" />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingInv ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : !(invoices || []).length ? (
                <div className="py-12 text-center text-muted-foreground">لا توجد فواتير</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>الطفل</TableHead>
                        <TableHead>العنوان</TableHead>
                        <TableHead>المبلغ</TableHead>
                        <TableHead>الحالة</TableHead>
                        <TableHead>التاريخ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(invoices || []).slice(0, 10).map(inv => (
                        <TableRow key={inv.id}>
                          <TableCell className="text-sm">{(inv as any).children?.full_name || "—"}</TableCell>
                          <TableCell className="text-sm">{inv.title}</TableCell>
                          <TableCell className="font-bold">{Number(inv.amount).toLocaleString()} ج.م</TableCell>
                          <TableCell>
                            <Badge variant={inv.status === "paid" ? "default" : inv.status === "overdue" ? "destructive" : "secondary"}>
                              {invoiceStatusLabels[inv.status] || inv.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{format(new Date(inv.created_at), "dd/MM/yyyy")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== PAYROLL ===== */}
        <TabsContent value="payroll">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">أحدث الرواتب</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/salary-report")}>
                عرض الكل <ArrowRight className="w-4 h-4 mr-1" />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingPay ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : !(payroll || []).length ? (
                <div className="py-12 text-center text-muted-foreground">لا توجد سجلات رواتب</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>الموظف</TableHead>
                        <TableHead>النوع</TableHead>
                        <TableHead>المبلغ</TableHead>
                        <TableHead>الحالة</TableHead>
                        <TableHead>الشهر</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(payroll || []).slice(0, 10).map(p => (
                        <TableRow key={p.id}>
                          <TableCell className="text-sm">{(p as any).staff?.full_name || "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {salaryTypeLabels[(p as any).salary_type || (p as any).staff?.salary_type || "monthly"] || "شهري"}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-bold">{Number(p.net_salary || 0).toLocaleString()} ج.م</TableCell>
                          <TableCell>
                            <Badge variant={p.status === "paid" ? "default" : "secondary"}>
                              {p.status === "paid" ? "مدفوع" : "معلق"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{p.month}/{p.year}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TRANSACTIONS ===== */}
        <TabsContent value="transactions">
          <div className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="بحث بالوصف أو اسم الموظف..." value={txSearch} onChange={e => setTxSearch(e.target.value)} className="pr-9" />
                  </div>
                  <Select value={txType} onValueChange={setTxType}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الأنواع</SelectItem>
                      <SelectItem value="invoice_payment">دفع فاتورة</SelectItem>
                      <SelectItem value="salary">راتب</SelectItem>
                      <SelectItem value="expense">مصروف</SelectItem>
                      <SelectItem value="refund">استرداد</SelectItem>
                      <SelectItem value="subscription">اشتراك</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={txDir} onValueChange={setTxDir}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">الكل</SelectItem>
                      <SelectItem value="in">وارد</SelectItem>
                      <SelectItem value="out">صادر</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleExportTx} variant="outline" size="sm" disabled={!filteredTx.length}>
                    <Download className="w-4 h-4 ml-1" /> تصدير
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardHeader><CardTitle className="text-lg">المعاملات ({filteredTx.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                {loadingTx ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : !filteredTx.length ? (
                  <div className="py-12 text-center text-muted-foreground">لا توجد معاملات مالية</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>التاريخ</TableHead>
                          <TableHead>النوع</TableHead>
                          <TableHead>الاتجاه</TableHead>
                          <TableHead>المبلغ</TableHead>
                          <TableHead>الوصف</TableHead>
                          <TableHead>الموظف</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTx.map(t => (
                          <TableRow key={t.id}>
                            <TableCell className="text-sm">{format(new Date(t.transaction_date), "dd/MM/yyyy")}</TableCell>
                            <TableCell><Badge variant="outline">{txTypeLabels[t.transaction_type] || t.transaction_type}</Badge></TableCell>
                            <TableCell>
                              {t.direction === "in" ? (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">وارد</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">صادر</Badge>
                              )}
                            </TableCell>
                            <TableCell className={`font-bold ${t.direction === "in" ? "text-green-600" : "text-destructive"}`}>
                              {t.direction === "in" ? "+" : "-"}{Number(t.amount).toLocaleString()} ج.م
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">{t.description || "—"}</TableCell>
                            <TableCell className="text-sm">{t.staff_name || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialCenter;
